import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import {  useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const TimeOffRequestsContext = createContext();
 
export const useTimeOffRequests = () => {
  const context = useContext(TimeOffRequestsContext);
  if (!context) {
    throw new Error('useTimeOffRequests must be used within a TimeOffRequestsProvider');
  }
  return context;
};

export const TimeOffRequestsProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError  } = useError();
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/time-off-requests');

  const getTimeOffRequests = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      if(filters?.completed){
        query += '&completed='+filters?.completed;
      } else{
        const completed = searchParams.get('completed') || '';
        query += '&completed='+completed;
      }
      let response = await apiInstance.call('time-off-requests?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.completed){
          query += '&completed='+filters?.completed;
        } else{
          const completed = searchParams.get('completed') || '';
          query += '&completed='+completed;
        }

        response = await apiInstance.call('time-off-requests?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setTimeOffRequests(response.data.timeOffRequests || []);
       
        
        if(filters){
          return response.data.timeOffRequests;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch time off requests information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getTimeOffRequestByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`time-off-requests/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let timeOffRequests = {'data':[response.data.timeOffRequest]}
      setTimeOffRequests(timeOffRequests || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/time-off-requests/edit/'+id))){
        if (getToken()) {
        
          
          await getTimeOffRequestByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getTimeOffRequestByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getTimeOffRequests(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getTimeOffRequests, searchParams, initialized,hideLoading,setApi]);

  const getTimeOffRequestsAddtionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-time-off-requests-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get time off requests data'
      });
    } 
  }, [getToken, handleApiError]);

  const addTimeOffRequest = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {

      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...data };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('time-off-requests', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the tasks list
        await getTimeOffRequests();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Time Off Request'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create time off request'
      });
      return { status: 500, error: error.message };
    }
  };

  const getTimeOffRequest = (id) => {
    return timeOffRequests?.data?.find(timeOffRequest => Number(timeOffRequest.id) === Number(id));
  };

  const getTimeOffRequestById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      console.log('Fetching time off request with ID:', id); // Debug log
      const response = await apiInstance.call(`time-off-requests/${id}`, 'GET', null, true);
      console.log('API Response:', response); // Debug log
      
      if (response.status === 200) {
        console.log('Response data:', response.data); // Debug log
        return { status: response.status, data: response.data.timeOffRequest };
      } else {
        console.error('API Error:', response.error); // Debug log
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch time off request details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      console.error('Exception in getTimeOffRequestById:', error); // Debug log
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch time off request details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateTimeOffRequest = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`time-off-requests/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the tasks list
        await getTimeOffRequests();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update time off request'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update time off request'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteTimeOffRequest = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`time-off-requests/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the tasks list
        await getTimeOffRequests();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete time off request'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete time off request'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    timeOffRequests,
    getTimeOffRequests,
    getTimeOffRequestById,
    getTimeOffRequest,
    addTimeOffRequest,
    createTimeOffRequest: addTimeOffRequest,
    updateTimeOffRequest,
    deleteTimeOffRequest,
    getTimeOffRequestsAddtionalData,
    isLoading
  };

  return (
    <TimeOffRequestsContext.Provider value={value}>
      {children}
    </TimeOffRequestsContext.Provider>
  );
};

export default TimeOffRequestsContext; 